import React, { useEffect, useState, useCallback } from 'react';
import { Coins, RefreshCw, AlertTriangle } from 'lucide-react';
import api from '../api';

interface TokenBalance {
  jetons_abonnement: number;
  jetons_abonnement_utilises: number;
  jetons_pay_as_you_go_restants: number;
}

const PappersCredits: React.FC = () => {
  const [balance, setBalance] = useState<TokenBalance | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);

  const fetchBalance = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/pappers/credits');
      setBalance(res.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur API Pappers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBalance();
    // Refresh every 5 minutes
    const interval = setInterval(fetchBalance, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchBalance]);

  // Subscription credits
  const subTotal = balance?.jetons_abonnement ?? 0;
  const subUsed = balance?.jetons_abonnement_utilises ?? 0;
  const subRemaining = subTotal - subUsed;

  // Pay-as-you-go credits
  const payg = balance?.jetons_pay_as_you_go_restants ?? 0;

  // Combined remaining
  const remaining = subRemaining + payg;
  const hasSubscription = subTotal > 0;
  const usagePercent = hasSubscription && subTotal > 0
    ? Math.round((subUsed / subTotal) * 100)
    : 0;

  const getStatusColor = () => {
    if (usagePercent >= 90) return 'var(--danger)';
    if (usagePercent >= 70) return 'var(--warning)';
    return 'var(--success)';
  };

  const getBarColor = () => {
    if (usagePercent >= 90) return 'var(--danger)';
    if (usagePercent >= 70) return 'var(--warning)';
    return 'var(--primary)';
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        className="pappers-credits-btn"
        onClick={() => setExpanded(!expanded)}
        title="Crédits Pappers"
      >
        <Coins size={16} style={{ color: getStatusColor() }} />
        {loading ? (
          <span className="pappers-credits-value">...</span>
        ) : error ? (
          <AlertTriangle size={14} style={{ color: 'var(--warning)' }} />
        ) : (
          <span className="pappers-credits-value" style={{ color: getStatusColor() }}>
            {remaining.toLocaleString('fr-FR')}
          </span>
        )}
      </button>

      {expanded && (
        <>
          <div className="pappers-credits-backdrop" onClick={() => setExpanded(false)} />
          <div className="pappers-credits-dropdown">
            <div className="pappers-credits-header">
              <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Crédits Pappers API</span>
              <button
                className="btn btn-sm"
                onClick={(e) => { e.stopPropagation(); fetchBalance(); }}
                disabled={loading}
                style={{ padding: '2px 6px' }}
              >
                <RefreshCw size={12} className={loading ? 'spin' : ''} />
              </button>
            </div>

            {error ? (
              <div style={{ padding: '12px', color: 'var(--danger)', fontSize: '0.85rem' }}>
                {error}
              </div>
            ) : balance ? (
              <div style={{ padding: '12px' }}>
                {hasSubscription && (
                  <>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Abonnement
                    </div>
                    <div className="pappers-credits-bar-container">
                      <div
                        className="pappers-credits-bar-fill"
                        style={{ width: `${usagePercent}%`, backgroundColor: getBarColor() }}
                      />
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '12px', textAlign: 'center' }}>
                      {subUsed.toLocaleString('fr-FR')} / {subTotal.toLocaleString('fr-FR')} ({usagePercent}% utilisé)
                    </div>
                  </>
                )}

                <div className="pappers-credits-grid">
                  {hasSubscription && (
                    <div className="pappers-credits-stat">
                      <div className="pappers-credits-stat-value" style={{ color: 'var(--primary)' }}>
                        {subRemaining.toLocaleString('fr-FR')}
                      </div>
                      <div className="pappers-credits-stat-label">Abo.</div>
                    </div>
                  )}
                  <div className="pappers-credits-stat">
                    <div className="pappers-credits-stat-value" style={{ color: 'var(--purple)' }}>
                      {payg.toLocaleString('fr-FR')}
                    </div>
                    <div className="pappers-credits-stat-label">Pay-as-you-go</div>
                  </div>
                  <div className="pappers-credits-stat">
                    <div className="pappers-credits-stat-value" style={{ color: 'var(--success)' }}>
                      {remaining.toLocaleString('fr-FR')}
                    </div>
                    <div className="pappers-credits-stat-label">Total dispo.</div>
                  </div>
                </div>

                {remaining <= 50 && (
                  <div className="pappers-credits-alert danger">
                    <AlertTriangle size={14} />
                    Attention : crédits bientôt épuisés !
                  </div>
                )}
                {remaining > 50 && remaining <= 150 && (
                  <div className="pappers-credits-alert warning">
                    <AlertTriangle size={14} />
                    Crédits restants limités
                  </div>
                )}
              </div>
            ) : (
              <div style={{ padding: '12px', textAlign: 'center' }}>
                <div className="spinner spinner-sm" />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PappersCredits;
